//Single source of truth for Estimate Item fields used in validation schemas
//Zmieniać tylko w Core
export enum AmountFieldType {
  sales = 'sales',
  purchase = 'purchase',
  others = 'others',
}

interface FieldDefinition {
  readonly name: string;
  readonly commission: boolean;
  readonly transferredCommission: boolean;
  readonly standard: boolean;
  readonly technicalCost: boolean;
}

interface AmountFieldDefinition extends FieldDefinition {
  readonly type: AmountFieldType;
}

interface PercentageFieldDefinition extends FieldDefinition {
  readonly type: AmountFieldType;
}

interface DictionaryFieldDefinition extends FieldDefinition {
  readonly dictionaryName: string;
}

export class EstimateItemFieldsDefinition {
  private constructor() {}

  static amountFieldsKeys: AmountFieldDefinition[] = [
    {
      name: 'rcSalesTotal',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesNet',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: true,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesNetTechnicalCost',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesBeerTax',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesPrintingCosts',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesOperatingFee',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesBlockSurcharge',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesTransferCosts',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesNetTotal',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: true,
      type: AmountFieldType.sales,
    },
    {
      name: 'salesCommissionAmount',
      commission: true,
      transferredCommission: true,
      standard: false,
      technicalCost: false,
      type: AmountFieldType.sales,
    },
    {
      name: 'rcPurchase',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchaseTechnicalCost',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchaseBeerTax',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchasePrintingCosts',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchaseOperatingFee',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchaseBlockSurcharge',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchaseTransferCosts',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchaseNetTotal',
      commission: false,
      transferredCommission: true,
      standard: true,
      technicalCost: true,
      type: AmountFieldType.purchase,
    },
    {
      name: 'purchaseNet',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.purchase,
    },
    {
      name: 'netCustomerAccount',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
      type: AmountFieldType.others,
    },
  ] as const;

  static percentageFieldsKeys: PercentageFieldDefinition[] = [
    {
      name: 'salesSurchargePercent',
      type: AmountFieldType.sales,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'salesDiscountPercent',
      type: AmountFieldType.sales,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'salesCommissionPercent',
      type: AmountFieldType.sales,
      commission: true,
      transferredCommission: true,
      standard: false,
      technicalCost: false,
    },
    {
      name: 'purchaseDiscountPercent',
      type: AmountFieldType.purchase,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'purchaseSurchargePercent',
      type: AmountFieldType.purchase,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'serviceExternalWorkPercent',
      type: AmountFieldType.others,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'serviceCreationPercent',
      type: AmountFieldType.others,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'serviceChangeProductionPercent',
      type: AmountFieldType.others,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'serviceStrategyPercent',
      type: AmountFieldType.others,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'serviceProductionBroadcastPercent',
      type: AmountFieldType.others,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'serviceClientServicePercent',
      type: AmountFieldType.others,
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
  ] as const;

  static stringFields: FieldDefinition[] = [
    {
      name: 'orderNumber',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'ownName',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'actionGroup',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'action',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'service',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'additionalDescription',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'numberOfMedia',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
  ] as const;

  static boolFields: FieldDefinition[] = [
    {
      name: 'isUpSell',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
  ] as const;

  static dictionaryFields: DictionaryFieldDefinition[] = [
    {
      name: 'copySize',
      dictionaryName: 'TV - długość kopii',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'purchaseMethod',
      dictionaryName: 'TV - sposób zakupu',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'purchaseFormInternet',
      dictionaryName: 'Internet - forma zakupu',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'categoryInternet',
      dictionaryName: 'Internet - kategoria',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'formatInternet',
      dictionaryName: 'Internet - format',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'labconChannel',
      dictionaryName: 'Labcon - kanał',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'categoryLabcon',
      dictionaryName: 'Labcon - kategoria',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'technologyDictionary',
      dictionaryName: 'OOH - technologia',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'regionDictionary',
      dictionaryName: 'OOH - rejon',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'formatPress',
      dictionaryName: 'Prasa - format',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'formatRadio',
      dictionaryName: 'Radio - format',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'emissionTypeRadio',
      dictionaryName: 'Radio - typ emisji',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'emissionTypeCinema',
      dictionaryName: 'Kino - typ emisji',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'medium',
      dictionaryName: 'Zagraniczne - medium',
      commission: false,
      transferredCommission: false,
      standard: true,
      technicalCost: false,
    },
    {
      name: 'countryDictionary',
      dictionaryName: 'Kraj/rynek',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: true,
    },
    {
      name: 'ballDictionary',
      dictionaryName: 'Produkt (Kulka)',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: true,
    },
    {
      name: 'pathPhaseDictionary',
      dictionaryName: 'Faza',
      commission: true,
      transferredCommission: true,
      standard: true,
      technicalCost: true,
    },
  ] as const;

  static get parametersFields(): readonly (
    | FieldDefinition
    | DictionaryFieldDefinition
  )[] {
    return [
      ...EstimateItemFieldsDefinition.stringFields,
      ...EstimateItemFieldsDefinition.boolFields,
      ...EstimateItemFieldsDefinition.dictionaryFields,
    ];
  }
}
